import { Injectable, signal } from '@angular/core';

export interface OktaUserInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Central cache for Okta user info.
 * Provides a lightweight lookup for display names
 */
@Injectable({
  providedIn: 'root'
})
export class OktaUserCacheService {
  // Use a Map for fast lookups
  private readonly _oktaUsers = new Map<string, OktaUserInfo>();
  // Optional reactive signal if you want the UI to update automatically
  readonly usersSignal = signal<Record<string, OktaUserInfo>>({});

  /** Add or update user info (no-op if user already cached) */
  addUser(oktaUserId: string, user: OktaUserInfo) {
    if (!oktaUserId || !user) return;
    if (!this._oktaUsers.has(oktaUserId)) {
      this._oktaUsers.set(oktaUserId, user);
      this.usersSignal.update(v => ({ ...v, [oktaUserId]: user }));
    }
  }

  /** Retrieve user by Okta ID */
  getUser(oktaUserId: string): OktaUserInfo | undefined {
    return this._oktaUsers.get(oktaUserId);
  }

  /** Bulk add users from a list of sessions */
  addUsersFromSessions(sessions: { oktaUserId?: string; oktaUser?: OktaUserInfo }[]) {
    const first = sessions[0];
    if (!first) return;

    const { oktaUserId, oktaUser } = first;
    if (oktaUserId && oktaUser && !this._oktaUsers.has(oktaUserId)) {
      this._oktaUsers.set(oktaUserId, oktaUser);
      this.usersSignal.update(v => ({ ...v, [oktaUserId]: oktaUser }));
    }
  }
}
